/* A nurse's skills, on her drawer in Team (owner, 2026-10-31: "the nurse
 * section skill's set would be work at the signing of nurse and it is also
 * upgradable by the agency"). She ticks the care she does when she signs up in
 * the partner app; here the agency adds or removes them. A job whose family
 * listed a care task is matched to staff who hold it — Assign marks the others
 * "Not trained for: …". Saving sends the whole set (PUT
 * /agency/staff/{id}/skills); a removed skill is switched off on the server,
 * never deleted. A skill is not a qualification: a task that needs a GNM
 * still needs a GNM. Nurses and attendants only. */

import { useEffect, useMemo, useState } from 'react';
import { getStaffSkills, setStaffSkills, type StaffSkill } from '../data/agency';
import { errorText } from '../data/words';
import { Btn, Card, CardHead, Empty, Note, Tag, uiCss } from '../ui';
import { useAsync } from './common';

const CATEGORY: Record<string, string> = {
  CLINICAL_PROCEDURE: 'Clinical procedures',
  MONITORING: 'Monitoring',
  PERSONAL_CARE: 'Personal care',
  THERAPY: 'Therapy',
};

/** The staff types that hold care-task skills (binding.SkillsForStaffType). */
export const holdsSkills = (staffType: string) => staffType === 'CLINICAL' || staffType === 'CARE';

export function StaffSkills({ staffId, name, manage }: { staffId: string; name: string; manage: boolean }) {
  const sheet = useAsync(() => getStaffSkills(staffId), [staffId]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const held = useMemo(() => new Set((sheet.data?.skills ?? []).filter((s) => s.granted).map((s) => s.code)), [sheet.data]);
  useEffect(() => setTicked(new Set(held)), [held]);

  const skills = sheet.data?.skills ?? [];
  const groups: [string, StaffSkill[]][] = [];
  for (const s of skills) {
    const g = groups.find(([c]) => c === s.category);
    if (g) g[1].push(s);
    else groups.push([s.category, [s]]);
  }
  const added = [...ticked].filter((c) => !held.has(c)).length;
  const removed = [...held].filter((c) => !ticked.has(c)).length;
  const changed = added + removed > 0;

  const toggle = (code: string, on: boolean) => {
    setMsg(null);
    setTicked((t) => {
      const n = new Set(t);
      if (on) n.add(code);
      else n.delete(code);
      return n;
    });
  };
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      // In the catalogue's order, so the request reads the way the list does.
      await setStaffSkills(staffId, skills.map((s) => s.code).filter((c) => ticked.has(c)));
      setMsg({ tone: 'ok', text: `Skills saved${name ? ` for ${name}` : ''}. Jobs that need them are offered from now.` });
      sheet.reload();
    } catch (e) {
      setMsg({ tone: 'danger', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHead
        title="Skills"
        sub={`${held.size} held. Jobs that list a care task are matched to staff who hold it. Ticked by them at sign-up; you add or remove them here.`}
      />
      {sheet.error ? (
        <div style={{ padding: '0 18px 14px' }}>
          <Note tone="danger">Their skills could not be read: {sheet.error}</Note>
        </div>
      ) : !sheet.data ? (
        <Empty>Reading…</Empty>
      ) : skills.length === 0 ? (
        <Empty>No care tasks are on the platform's list yet.</Empty>
      ) : (
        <div>
          {sheet.data.custom?.length ? (
            <div style={{ padding: '10px 18px' }}>
              <div className={uiCss.label}>In their own words</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {sheet.data.custom.map((c) => (
                  <Tag key={c.public_id} fg="var(--ink)" bg="var(--line-soft)">{c.name}</Tag>
                ))}
              </div>
            </div>
          ) : null}
          {groups.map(([cat, list]) => (
            <fieldset key={cat} style={{ border: 0, margin: 0, padding: 0 }}>
              <legend className={uiCss.label} style={{ padding: '10px 18px 4px' }}>
                {CATEGORY[cat] ?? cat.replace(/_/g, ' ').toLowerCase()}
              </legend>
              {list.map((s) => {
                const on = ticked.has(s.code);
                return (
                  <label
                    key={s.code}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '10px 18px',
                      minHeight: 44,
                      borderTop: '1px solid var(--line-soft)',
                      cursor: manage ? 'pointer' : 'default',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!manage || busy}
                      onChange={(e) => toggle(s.code, e.target.checked)}
                      style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
                    />
                    <span className={uiCss.grow}>
                      <span style={{ display: 'block', font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{s.name}</span>
                      {s.description ? <span className={uiCss.sub}>{s.description}</span> : null}
                    </span>
                    {s.granted && !s.signed_off ? (
                      <Tag fg="var(--amber)" bg="var(--amber-bg)">
                        Their own pick
                      </Tag>
                    ) : s.granted ? (
                      <Tag fg="var(--ok)" bg="var(--ok-bg)">
                        Signed off
                      </Tag>
                    ) : null}
                  </label>
                );
              })}
            </fieldset>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 18px 16px', borderTop: '1px solid var(--line-soft)' }}>
        {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}
        {manage ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={uiCss.sub} style={{ flex: 1 }}>
              {changed ? [added ? `${added} to add` : '', removed ? `${removed} to remove` : ''].filter(Boolean).join(' · ') : 'Tick a skill to add it; untick to remove it.'}
            </span>
            <Btn disabled={!changed || busy} onClick={() => setTicked(new Set(held))}>
              Undo
            </Btn>
            <Btn variant="primary" disabled={!changed || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save skills'}
            </Btn>
          </div>
        ) : (
          <span className={uiCss.sub}>An owner or admin changes skills.</span>
        )}
      </div>
    </Card>
  );
}
